import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useContext } from 'react';

import fortress from '../api/fortress';
import { AuthContext } from '../contexts/AuthContext';
import { BrandContext } from '../contexts/BrandContext';
import { useGlobalStore } from '../store/store';

export interface useTopProductPerformanceProps {
  top?: number;
  order?: string;
}

export const useTopProductPerformance = ({
  top = 5,
  order = 'desc',
}: useTopProductPerformanceProps) => {
  const { token } = useContext(AuthContext);
  const { brand } = useContext(BrandContext);
  const { dates, currency, marketplaces } = useGlobalStore();

  const params = {
    brandid: brand.brandId,
    tf_s: format(dates.tf_s, 'yyyy-MM-dd'),
    tf_e: format(dates.tf_e, 'yyyy-MM-dd'),
    currency: currency,
    marketplace: marketplaces,
    top: top,
    order: order,
  };

  return useQuery({
    queryKey: ['ProductPerformance', params, token],
    queryFn: () => {
      return fortress.get<ITopProductPerformanceResponse>(
        'brand/performance/performanceTopProducts',
        {
          headers: { Authorization: `Bearer ${token}` },
          params: params,
        },
      );
    },
    staleTime: 1000 * 30,
    // enabled: false,
    throwOnError: () => {
      return false;
    },
  });
};

export interface ITopProductPerformanceResponse {
  info: {
    tf: {
      tf_s: string;
      tf_e: string;
      tf_total_sales: number;
      days_diff: number;
    };
    py: {
      py_s: string;
      py_e: string;
      days_diff: number;
    };
    ly: {
      ly_s: string;
      ly_e: string;
      days_diff: number;
    };
  };
  tableData: ITopProductPerformanceTableData[];
}

export interface ITopProductPerformanceTableData {
  index: number;
  vpart: string;
  upc: string;
  asin: [
    {
      asin: string;
      asinLink: string;
    },
  ];
  image: string;
  short_title: string;
  total_sales: number;
  py_total_sales: number;
  pct_brand_total_sales: number;
  previous_year_change_pct: number;
  sparkline_sales: {
    date: string;
    value: number;
  }[];
}
